import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  DuplicateTicketNumberSequenceMutation as DuplicateTicketNumberSequence,
  GetTicketNumberSequencesForEventQuery as GetTicketNumberSequencesForEvent,
} from '__generated__/graphql';

import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import DuplicateTicketNumberSequenceMutation from './DuplicateTicketNumberSequenceMutation';
import UI from '../../../common/UI';
import useIntegerInput from '../../../common/useIntegerInput';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

export type DuplicatedTicketNumberSequence = DuplicateTicketNumberSequence['duplicateTicketNumberSequence'];

type TicketNumberSequence = GetTicketNumberSequencesForEvent['project']['event']['ticket_number_sequences'][0];
const duplicateTicketNumberSequenceSchema = yup.object({
  title: yup.string().ensure().required(),
});

export interface DuplicateTicketNumberSequenceFormProps {
  sequence: TicketNumberSequence;
  onSuccess?: (sequence: DuplicatedTicketNumberSequence) => void;
}

export const DuplicateTicketNumberSequenceForm = ({
  sequence, onSuccess,
}: DuplicateTicketNumberSequenceFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const integerInput = useIntegerInput();

  const getValues = (sequence: TicketNumberSequence) => ({
    title: sequence.title,
    start_number: sequence.start_number + sequence.capacity || 0,
    prefix: sequence.prefix,
    suffix: sequence.suffix,
  });

  const form = useForm({
    values: getValues(sequence),
  });

  const [
    duplicateTicketNumberSequence, { loading, error },
  ] = useMutation(DuplicateTicketNumberSequenceMutation, {
    variables: {
      input: {
        id: sequence.id,
        info: form.values,
      },
    },
    onCompleted: ({ duplicateTicketNumberSequence: sequence }) => {
      onSuccess?.(sequence);
      notifier.success(t('notifications.ticket_number_sequence_duplicated'));
    },
  });

  const { valid, errors } = useValidation({
    schema: duplicateTicketNumberSequenceSchema,
    values: {
      sequence: { id: sequence.id },
      ...form.values,
    },
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => duplicateTicketNumberSequence()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('duplicate_ticket_number_sequence', { sequence: sequence.title })}
          </UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('duplicate_ticket_number_sequence_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="SequenceTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            placeholder={t('ticket_number_sequence_title_placeholder')}
            autoFocus={!sequence}
            id="SequenceTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.prefix, touched: form.touched.prefix }}>
            <UI.InputLabel htmlFor="SequencePrefix">
              {t('prefix')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('prefix')}
              maxLength={3}
              id="SequencePrefix"
            />
            <UI.ErrorMessages attribute={t('prefix')} errors={errors.prefix} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.start_number, touched: form.touched.start_number }}>
            <UI.InputLabel htmlFor="SequenceStartNumber">
              {t('start_number')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('start_number', {
                inputHandler: integerInput(),
              })}
              format={integerInput().format}
              maxLength={8}
              inputMode="numeric"
              match={/[\d]/g}
              id="SequenceStartNumber"
            />
            <UI.ErrorMessages attribute={t('start_number')} errors={errors.start_number} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.suffix, touched: form.touched.suffix }}>
            <UI.InputLabel htmlFor="SequenceSuffix">
              {t('suffix')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('suffix')}
              maxLength={3}
              id="SequenceSuffix"
            />
            <UI.ErrorMessages attribute={t('suffix')} errors={errors.suffix} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('duplicate')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

// eslint-disable-next-line max-len
interface UseDuplicateTicketNumberSequenceModalProps extends DuplicateTicketNumberSequenceFormProps {}

const useDuplicateTicketNumberSequenceModal = ({
  sequence, onSuccess,
}: UseDuplicateTicketNumberSequenceModalProps) => {
  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = (sequence: DuplicatedTicketNumberSequence) => {
      closeModal();
      onSuccess?.(sequence);
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        <DuplicateTicketNumberSequenceForm sequence={sequence} onSuccess={handleSuccess} />
      </UI.Modal>
    );
  }, [sequence]);

  return [openModal, closeModal];
};

export default useDuplicateTicketNumberSequenceModal;
